import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { BackHeader } from '../components/BackHeader';
import ConfirmModal from '../components/ui/ConfirmModal';
import { Loader, RotateCcw, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getJobTypeLabel } from '../constants/job-types';

export default function DeletedJobsScreen() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [restoreTarget, setRestoreTarget] = useState<{ id: string; jobNumber: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['deleted-jobs'],
    queryFn: () => api.jobs.listDeleted().then(r => r.data.data ?? []),
  });

  const jobs: any[] = data ?? [];

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.jobs.restore(id),
    onSuccess: () => {
      toast.success('Job restored successfully');
      setRestoreTarget(null);
      queryClient.invalidateQueries({ queryKey: ['deleted-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: () => {
      toast.error('Failed to restore job');
    },
  });

  const filtered = search
    ? jobs.filter((j: any) => {
        const s = search.toLowerCase();
        const jobNum = (j.job_number || j.jobNumber || '').toLowerCase();
        const client = (j.client_name || j.clientName || '').toLowerCase();
        const rig = (j.rig_name || j.rigName || '').toLowerCase();
        const well = (j.well_name || j.wellName || '').toLowerCase();
        return jobNum.includes(s) || client.includes(s) || rig.includes(s) || well.includes(s);
      })
    : jobs;

  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    try {
      return format(new Date(d), 'MMM d, yyyy h:mm a');
    } catch {
      return '—';
    }
  };

  return (
    <>
      <BackHeader
        to="/jobs"
        label="Jobs"
        title="Deleted Jobs"
        subtitle={`${filtered.length} deleted job${filtered.length !== 1 ? 's' : ''}`}
      />

      <div className="px-6 py-4">
        {/* Search */}
        <div className="relative mb-4 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-fieldiq-muted" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deleted jobs…"
            className="w-full pl-9 pr-8 py-2 rounded-lg bg-fieldiq-dark border border-fieldiq-border text-white placeholder-fieldiq-muted text-sm focus:outline-none focus:ring-1 focus:ring-fieldiq-accent"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-fieldiq-muted hover:text-white">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader size={24} className="animate-spin text-fieldiq-accent" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Trash2 size={48} className="text-fieldiq-muted mb-4" />
            <h3 className="text-lg font-semibold text-white mb-1">No deleted jobs</h3>
            <p className="text-sm text-fieldiq-muted">
              {search ? 'No deleted jobs match your search.' : 'Jobs that are deleted will appear here for recovery.'}
            </p>
          </div>
        )}

        {/* Table */}
        {!isLoading && filtered.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-fieldiq-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border bg-fieldiq-dark/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Job #</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Rig / Well</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Deleted</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Deleted By</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Reason</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-fieldiq-muted uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fieldiq-border">
                {filtered.map((job: any) => {
                  const id = job.id;
                  const jobNumber = job.job_number || job.jobNumber || '—';
                  const clientName = job.client_name || job.clientName || '—';
                  const rigName = job.rig_name || job.rigName || '';
                  const wellName = job.well_name || job.wellName || '';
                  const rigWell = [rigName, wellName].filter(Boolean).join(' / ') || '—';
                  const jobType = job.job_type || job.jobType || '';
                  const deletedAt = job.deleted_at || job.deletedAt;
                  const deletedBy = job.deleted_by || job.deletedBy || '—';
                  const deletedReason = job.deleted_reason || job.deletedReason || '—';

                  return (
                    <tr key={id} className="hover:bg-white/5 transition-colors">
                      <td className="px-4 py-3 font-medium text-white">{jobNumber}</td>
                      <td className="px-4 py-3 text-gray-300">{clientName}</td>
                      <td className="px-4 py-3 text-gray-300">{rigWell}</td>
                      <td className="px-4 py-3 text-gray-300">{getJobTypeLabel(jobType)}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(deletedAt)}</td>
                      <td className="px-4 py-3 text-gray-300">{deletedBy}</td>
                      <td className="px-4 py-3 text-gray-400 max-w-[200px] truncate" title={deletedReason}>{deletedReason}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setRestoreTarget({ id, jobNumber })}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fieldiq-accent/10 text-fieldiq-accent hover:bg-fieldiq-accent/20 text-xs font-medium transition-colors"
                        >
                          <RotateCcw size={13} />
                          Restore
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={restoreTarget !== null}
        title="Restore Job?"
        body={`Are you sure you want to restore job ${restoreTarget?.jobNumber ?? ''}? It will reappear in the Jobs list.`}
        confirmLabel="Restore"
        confirmVariant="primary"
        loading={restoreMutation.isPending}
        onConfirm={() => restoreTarget && restoreMutation.mutate(restoreTarget.id)}
        onCancel={() => { setRestoreTarget(null); restoreMutation.reset(); }}
      />
    </>
  );
}
